/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonButton, IonPage, IonToast, useIonViewWillEnter } from '@ionic/react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from '../App';
import { useOverUserLimitQuery } from '../api/SiteSettingsAPI';
import { Input } from '../components/Forms/Input';
import { IconButton } from '../components/IconButton';
import { IconArrowBack, IconGlobe } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { SiteLogoBanner } from '../components/SiteLogoBanner';
import { useLoading } from '../hooks/useQuirkLoading';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { LoginState, authenticate, setEmail, setLoginError, setPassword } from '../state/loginSlice';
import { removeAnonEmail } from '../util/removeAnonEmail';
import { AnonymousOnly } from './admin/AnonymousOnly';
// eslint-disable-next-line css-modules/no-unused-class
import sharedStyles from '../styles/Shared.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import onboardingStyles from '../styles/onboarding.module.css';
import styles from './Login.module.css';
import { useBackendDomain } from '../hooks/useBackendDomain.js';
import { useDevice } from '../hooks/useDevice.js';

export const Login = () => {
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const authenticated = state.loggedIn === 'valid';
	const loggedIn = state.loggedIn;
	useLoading({ message: 'Please wait...', isLoading: loggedIn === 'validating' });
	const [domain, setDomain] = useBackendDomain();
	const device = useDevice();
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const ref = useCallback((node: HTMLInputElement) => {
		node?.focus();
	}, []);
	const history = useHistory();
	const { data: limit } = useOverUserLimitQuery();
	const anonymous = useUserHasRole('anonymous');
	useIonViewWillEnter(() => {
		dispatch(setEmail(''));
		dispatch(setPassword(''));
	});
	const goBack = () => {
		if (history.length <= 1) {
			history.push('/main/latest');
		} else {
			history.go(-1);
		}
	};
	const login = () => {
		const { password } = state.user;
		const email = removeAnonEmail(state.user.email);
		dispatch(authenticate({ email, password }));
	};
	if (authenticated && !anonymous) {
		window.location.href = '/main/latest';
	}
	return (
		<IonPage data-testid="loginPage">
			<QuirkIonContent safeAreaTop>
				<div
					onKeyDown={event => {
						event.key === 'Enter' && login();
					}}
					className="login-container"
				>
					<AnonymousOnly>
						<div className={styles.backButton} onClick={goBack}>
							<IconButton icon={<IconArrowBack />} />
						</div>
					</AnonymousOnly>

					<SiteLogoBanner siteName={true} />
					{state.domain && state.platform !== 'web' && (state.communities?.length ?? 0) > 1 && (
						<IconButton
							label={'Switch Community'}
							icon={<IconGlobe />}
							onClick={() => {
								history.push('/main/settings/community');
							}}
						/>
					)}
					<div className="space20" />
					{limit && limit.overUserLimit ? (
						<div className={onboardingStyles.contentWrapper}>
							<div className={onboardingStyles.overlimitMessage}>
								<p>We are currently not accepting new registrations.</p>
								<p>Please check back soon!</p>
							</div>
						</div>
					) : (
						<form>
							<div className={sharedStyles.contentWrapper}>
								<div className={styles.formContainer}>
									<Input
										inputRef={ref}
										placeholder="Email"
										label="Email"
										type="email"
										tabIndex={1}
										value={removeAnonEmail(state.user.email)}
										onChange={event => {
											dispatch(setEmail(event.target.value));
										}}
									/>
									<Input
										placeholder="Password"
										type="password"
										label="Password"
										tabIndex={2}
										value={state.user.password}
										onChange={event => {
											dispatch(setPassword(event.target.value));
										}}
									/>
									{device?.platform !== 'web' &&
										state.user.email.endsWith('quirklocal.com') &&
										state.user.password === 'QU1RKs3kR3T' && (
											<Input
												placeholder="Domain"
												type="url"
												label="Domain"
												tabIndex={3}
												value={domain || ''}
												onChange={event => {
													setDomain(event.target.value);
												}}
											/>
										)}
									<div className={onboardingStyles.btnUnderPadding}>
										<a
											onClick={() => {
												history.push('/main/settings/forgotPassword');
											}}
											data-testid="loginForgotPasswordLink"
										>
											Forgot Password
										</a>
									</div>
								</div>

								<IonToast
									isOpen={state.loginError !== ''}
									duration={1500}
									message={state.loginError}
									position="top"
									color="danger"
									onDidDismiss={() => dispatch(setLoginError(''))}
								/>

								<div className={styles.loginButton}>
									<IonButton
										data-testid="loginPageLoginButton"
										size="large"
										expand="block"
										onClick={login}
										tabIndex={3}
									>
										Login
									</IonButton>
								</div>
								<div className={onboardingStyles.signupModuleContainer}>
									<div className={onboardingStyles.signupModule}>
										<div>Don’t have an account?</div>

										<div className={onboardingStyles.btnUnderPadding}>
											<IonButton
												color="primary"
												fill="solid"
												size="default"
												className="radius8"
												onClick={() => {
													history.push('/main/settings/signup');
												}}
												expand="block"
											>
												Sign up
											</IonButton>
										</div>

										<div>
											<a
												onClick={() => {
													history.push('/main/settings/emailLogin/request');
												}}
												data-testid="loginPageEmailLoginLink"
											>
												Passwordless Login (Email)
											</a>
										</div>
									</div>
								</div>
							</div>
						</form>
					)}
				</div>
			</QuirkIonContent>
		</IonPage>
	);
};
